import { Controller } from '@hotwired/stimulus'
import { FileUpload, UserDataValue } from '../../api/models/types'
import updateValidationUI, {
  ValidationParent,
} from '../../helpers/controllerHelpers/customValidationUI'
import {
  connectUserData,
  doNotStoreComponentDataInCloud,
  getComponentContext,
  lifecycleTrigger,
} from '../../helpers/controllerHelpers/utils'
import { env } from '../../helpers/env'
import jitsu from '../../helpers/jitsu'
import { generateId, initAndWaitForSupabaseClient } from '../../helpers/utils'
import { cloneDeep } from 'lodash'
/*
NYI
* Drag and Drop
* Validation
* Multiple Uploads?
* Possible missing classes
* Default Styles
* Upload Types

*/

const WORKER_NAME = 'upload-to-r2-bucket--public-flow-assets'

export default class extends Controller {
  static targets = ['uploader', 'validation', 'filelist']
  static values = { validation: Boolean }

  disconnectUserData?: () => void
  flowId: string | null
  component: FileUpload
  declare hasFilelistTarget: boolean
  declare filelistTarget: HTMLElement
  declare userDataValue: UserDataValue
  flowEl: Element | null

  connect() {
    const flowEl = this.element.closest(`[data-controller*="flow"]`)
    this.flowId = flowEl && flowEl.getAttribute('data-flow-id-value')
    const { component } = getComponentContext(this)
    this.component = component as FileUpload
    this.disconnectUserData = connectUserData(this, this.userDataValueChanged)
    this.flowEl = flowEl
    lifecycleTrigger(this, 'mounted')
  }

  disconnect() {
    lifecycleTrigger(this, 'unmounted')
    if (typeof this.disconnectUserData === 'function') this.disconnectUserData()
  }

  userDataValueChanged(newData: { [x: string]: { status: string } }) {
    const key = this.element.getAttribute('data-component-key-value')
    if (key && newData?.[key]) {
      const message = this.getValidationMessage(newData[key])
      updateValidationUI(
        this,
        message,
        this.element as ValidationParent,
        this.element as HTMLObjectElement
      )
      this.updateUI(newData[key].status, newData[key])
    }
  }

  getValidationMessage(key: { status: string }) {
    const isValid = this.component && this.component.isRequired ? Boolean(key) : true
    if (isValid) return ''
    if (!key && this.component.empty_invalid_message) return this.component.empty_invalid_message
    if (key && this.component.invalid_message) return this.component.invalid_message
    switch (this.component.validation_formula) {
      default:
        return 'This field is required.'
    }
  }

  update(fileData: { id: string; name?: string; status: string; url?: string }) {
    const componentKey = this.element.getAttribute('data-component-key-value') || ''
    let value = cloneDeep(this.userDataValue[componentKey])
    if (this.component.multiple) {
      if (!value) value = []
      if (!Array.isArray(value)) value = [value]
      const index = value.findIndex((item) => item.id === fileData.id)
      if (index > -1) {
        if (fileData.status === 'deleted') value.splice(index, 1)
        else value[index] = fileData
      } else {
        if (fileData.status !== 'deleted') value.push(fileData)
      }
    } else {
      value = fileData.status !== 'deleted' ? fileData : null
    }

    this.dispatch('update', {
      detail: {
        key: componentKey,
        value,
        callback: () => this.triggerOnChangeActions(value),
      },
    })
    if (!doNotStoreComponentDataInCloud(this.component)) {
      jitsu.track('file:uploaded', {
        flow_id: this.flowId,
        file_uploader_id: this.component.id,
        file_uploader_key: this.component.key,
      })
    }
  }

  async uploadFile(event: { target: { files: any[]; value: string | null } }) {
    const file = event.target.files[0]

    const fileId = generateId('file')

    //To test locally comment 'local' out
    if (['dev', 'ci', 'test', 'local'].includes('env()')) {
      const url = URL.createObjectURL(file)
      this.updateUI('done', file)
      this.update({ id: fileId, name: file.name, status: 'in_memory', url: url || 'in_memory' })
      return
    }

    if (this.component.uploadFile) {
      const fileName = `${file.name.split('.')[0]}_${('_' + Math.random()).slice(3)}.${file.name
        .split('.')
        .slice(1)
        .join('.')}`.replace(/[^a-zA-Z0-9-_.]/g, '')

      function updateStatus(status: string, url?: string) {
        this.update({
          id: fileId,
          name: fileName,
          status,
          url,
        })
      }

      this.uploadToSupabase(file, fileName, updateStatus.bind(this))
      this.uploadToCloudflare(file, fileName, updateStatus.bind(this))

      event.target.value = null
    }
  }

  async uploadToCloudflare(
    file: File,
    fileName: string,
    updateStatus: (status: string, url?: string) => void
  ) {
    updateStatus('uploading')
    return await fetch(
      `https://${WORKER_NAME}.heysavvy.workers.dev/${decodeURIComponent(fileName)}`,
      {
        method: 'POST',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        updateStatus('done', data.data.url)
      })
      .catch((error) => {
        console.error('Error Uploading to Cloudflare:', error)
        updateStatus('error')
      })
  }

  async uploadToSupabase(
    file: File,
    fileName: string,
    updateStatus: (status: string, url?: string) => void
  ) {
    const supabaseClient = await initAndWaitForSupabaseClient()
    const { data, error } = await supabaseClient.storage
      .from('uploaded_assets')
      .upload(`${this.flowId}/${fileName}`, file)

    if (error) {
      console.error(`Error Uploading ${fileName}:`, error)
    } else {
      try {
        const { data: uploadedData, error: uploadError } = await supabaseClient.storage
          .from('uploaded_assets')
          .getPublicUrl(`${this.flowId}/${fileName}`)

        if (uploadError) {
          console.error(uploadError)
          return
        }

        const url = uploadedData.publicUrl
      } catch (error) {
        console.error(error)
      }
    }
  }

  updateUI(status: string, fileOrFiles: { status?: string; name?: string }) {
    /* Create / reuse existing validation element here */
    if (!this.hasFilelistTarget) return
    const files = Array.isArray(fileOrFiles) ? [...fileOrFiles] : [fileOrFiles]

    // For loop - over files
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      // Your code here

      let itemElement = this.filelistTarget.querySelector(`[data-file-id="${file.id}"]`)

      if (itemElement) {
        const fileName = itemElement.querySelector('.file-name') as HTMLElement
        const downloadLinkA = itemElement.querySelector('.paper-clip') as HTMLElement
        if (fileName) {
          fileName.innerText = String(file.name)
          fileName.setAttribute('data-name', String(file.name))
        }
        itemElement.setAttribute('data-status', status)
        if (downloadLinkA)
          downloadLinkA.style.display = file.status === 'uploading' ? 'none' : 'inline-block'
        if (status === 'deleted') itemElement.remove()
      } else {
        const item = document.createElement('span')
        item.classList.add('ant-upload-list-item-info')
        item.setAttribute('data-file-id', String(file.id))
        item.style.position = 'relative'
        item.style.display = 'block'

        const downloadLinkA = document.createElement('a')
        downloadLinkA.ariaLabel = 'icon: paper-clip'
        downloadLinkA.classList.add('paper-clip', 'anticon', 'anticon-paper-clip')
        downloadLinkA.style.display = file.status === 'uploading' ? 'none' : 'inline-block'

        downloadLinkA.addEventListener('click', (event) => {
          event.preventDefault()
          event.stopPropagation()
          const url =
            this.userDataValue[Number(this.element.getAttribute('data-component-key-value'))].url
          if (url) window.open(url, '_blank')
        })

        const paperClipSvg = document.createElement('svg')
        paperClipSvg.setAttribute('data-controller', 'icon')
        paperClipSvg.setAttribute('data-icon-name-value', 'paperclip')
        paperClipSvg.setAttribute('data-icon-library-value', 'fa')
        paperClipSvg.classList.add('Icon')
        paperClipSvg.setAttribute('viewBox', '64 64 896 896')
        paperClipSvg.setAttribute('aria-hidden', 'true')
        paperClipSvg.setAttribute('focusable', 'false')
        paperClipSvg.setAttribute('fill', 'currentColor')
        paperClipSvg.setAttribute('height', '1em')
        paperClipSvg.setAttribute('width', '1em')
        // paperClipSvg.setAttribute('data-icon', 'paper-clip')

        const path = document.createElement('path')
        path.setAttribute(
          'd',
          'M779.3 196.6c-94.2-94.2-247.6-94.2-341.7 0l-261 260.8c-1.7 1.7-2.6 4-2.6 6.4s.9 4.7 2.6 6.4l36.9 36.9a9 9 0 0 0 12.7 0l261-260.8c32.4-32.4 75.5-50.2 121.3-50.2s88.9 17.8 121.2 50.2c32.4 32.4 50.2 75.5 50.2 121.2 0 45.8-17.8 88.8-50.2 121.2l-266 265.9-43.1 43.1c-40.3 40.3-105.8 40.3-146.1 0-19.5-19.5-30.2-45.4-30.2-73s10.7-53.5 30.2-73l263.9-263.8c6.7-6.6 15.5-10.3 24.9-10.3h.1c9.4 0 18.1 3.7 24.7 10.3 6.7 6.7 10.3 15.5 10.3 24.9 0 9.3-3.7 18.1-10.3 24.7L372.4 653c-1.7 1.7-2.6 4-2.6 6.4s.9 4.7 2.6 6.4l36.9 36.9a9 9 0 0 0 12.7 0l215.6-215.6c19.9-19.9 30.8-46.3 30.8-74.4s-11-54.6-30.8-74.4c-41.1-41.1-107.9-41-149 0L463 364 224.8 602.1A172.22 172.22 0 0 0 174 724.8c0 46.3 18.1 89.8 50.8 122.5 33.9 33.8 78.3 50.7 122.7 50.7 44.4 0 88.8-16.9 122.6-50.7l309.2-309C824.8 492.7 850 432 850 367.5c.1-64.6-25.1-125.3-70.7-170.9z'
        )

        paperClipSvg.appendChild(path)
        downloadLinkA.appendChild(paperClipSvg)
        item.appendChild(downloadLinkA)

        const fileName = document.createElement('span')
        fileName.innerText = String(file.name)
        fileName.setAttribute('data-name', String(file.name))
        fileName.classList.add('file-name', 'ant-upload-list-item-name')
        item.appendChild(fileName)

        const fileActions = document.createElement('span')
        fileActions.classList.add('file-actions', 'ant-upload-list-item-card-actions')

        const anchor = document.createElement('a')
        anchor.addEventListener('click', (e) => {
          e.preventDefault()
          this.update({ id: file.id, status: 'deleted' })
          item.remove()
        })

        const italicAnchorEl = document.createElement('i')
        italicAnchorEl.ariaLabel = 'icon: delete'
        italicAnchorEl.classList.add('anticon', 'anticon-delete', 'delete-file')
        italicAnchorEl.tabIndex = -1

        const anchorSvg = document.createElement('svg')
        anchorSvg.setAttribute('data-controller', 'icon')
        anchorSvg.setAttribute('data-icon-name-value', 'trash')
        anchorSvg.setAttribute('data-icon-library-value', 'fa')
        anchorSvg.classList.add('Icon')
        anchorSvg.setAttribute('viewBox', '64 64 896 896')
        anchorSvg.setAttribute('data-icon', 'delete')
        anchorSvg.setAttribute('width', '1em')
        anchorSvg.setAttribute('height', '1em')
        anchorSvg.setAttribute('fill', 'currentColor')
        anchorSvg.setAttribute('aria-hidden', 'true')
        anchorSvg.setAttribute('focusable', 'false')

        const anchorPath = document.createElement('path')
        anchorPath.setAttribute(
          'd',
          'M360 184h-8c4.4 0 8-3.6 8-8v8h304v-8c0 4.4 3.6 8 8 8h-8v72h72v-80c0-35.3-28.7-64-64-64H352c-35.3 0-64 28.7-64 64v80h72v-72zm504 72H160c-17.7 0-32 14.3-32 32v32c0 4.4 3.6 8 8 8h60.4l24.7 523c1.6 34.1 29.8 61 63.9 61h454c34.2 0 62.3-26.8 63.9-61l24.7-523H888c4.4 0 8-3.6 8-8v-32c0-17.7-14.3-32-32-32zM731.3 840H292.7l-24.2-512h487l-24.2 512z'
        )

        anchorSvg.appendChild(anchorPath)
        italicAnchorEl.appendChild(anchorSvg)
        anchor.appendChild(italicAnchorEl)
        fileActions.appendChild(anchor)
        item.appendChild(fileActions)
        if (!this.component.multiple) {
          this.filelistTarget.innerHTML = ''
        }
        this.filelistTarget.appendChild(item)
        itemElement = item
      }
    }

    switch (status) {
      case 'error': {
        break
      }
      case 'uploading': {
        break
      }
      case 'done': {
        break
      }
      case 'ready':
      default: {
        break
      }
    }
  }

  triggerOnChangeActions(newValue: any) {
    if (!newValue) newValue = null
    //* I trigger onchange actions after waiting for the userData update to complete
    if (this.component.outputs_onchange) {
      const actions = this.component.outputs_onchange
      this.dispatch('actions', {
        detail: {
          actions: actions,
          component: this.component,
        },
      })
    }
  }

  async accessibilityFileUpload(event) {
    //*Preventing scrolling
    if (
      (event as KeyboardEvent).key === ' ' ||
      (event as KeyboardEvent).key === 'ArrowDown' ||
      (event as KeyboardEvent).key === 'ArrowUp'
    )
      event.preventDefault()

    if ((event as KeyboardEvent).key === 'Enter' || (event as KeyboardEvent).key === ' ') {
      const input = this.element.querySelector('input')
      input?.click()
    }
  }
}
